/**
 * Pure geometry for the life panel's P10/median/P90 band chart
 * (DIRECTION.md §1.6) — a compact visual on top of the existing honest
 * stat table (unchanged, still the source of truth for the exact numbers).
 * Scales the three values onto a 0-100% track so the DOM layer only has to
 * set `left`/`width` percentages; no chart-library dependency.
 *
 * Never fabricates a layout from incomplete data: any of the three values
 * being `null`, or a nonsensical ordering (P10 above median, median above
 * P90 — the model's own inputs disagreeing with themselves), returns
 * `null` rather than drawing a misleading band.
 */

export interface LifeBandLayout {
  readonly p10Pct: number;
  readonly medianPct: number;
  readonly p90Pct: number;
}

export function computeLifeBandLayout(
  p10: number | null,
  median: number | null,
  p90: number | null,
): LifeBandLayout | null {
  if (p10 === null || median === null || p90 === null) return null;
  if (!Number.isFinite(p10) || !Number.isFinite(median) || !Number.isFinite(p90)) return null;
  if (p10 > median || median > p90) return null; // inputs disagree with themselves — don't draw
  if (p90 <= 0) return null; // nothing to scale against

  const max = p90;
  const clamp = (n: number): number => Math.max(0, Math.min(100, (n / max) * 100));
  return { p10Pct: clamp(p10), medianPct: clamp(median), p90Pct: clamp(p90) };
}
